import { useEffect, useState } from "react";
import { Ticket, CaretDown, CaretUp, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const SupportInbox = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("open");

  const load = async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data);
    } catch (_e) {
      setHidden(true);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (user?.is_owner) load();
    else { setHidden(true); setLoaded(true); }
  }, [user?.is_owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/support/tickets/${id}`, { status });
      setTickets((t) => t.map((x) => (x.id === id ? data : x)));
      toast.success(status === "resolved" ? "Ticket resolved" : "Ticket reopened");
    } catch (_e) {
      toast.error("Could not update ticket");
    }
  };

  if (!loaded || hidden) return null;
  const visible = tickets.filter((t) => (filter === "all" ? true : t.status === filter));
  const openCount = tickets.filter((t) => t.status === "open").length;

  return (
    <section className="card-flat p-6" data-testid="support-inbox-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Ticket size={20} weight="duotone" className="text-brand-light" />
          <div>
            <h2 className="font-display text-xl">Support inbox</h2>
            <p className="tracker text-zinc-500 text-[10px]">Escalated coach conversations — {openCount} open</p>
          </div>
        </div>
        <div className="flex gap-1">
          {["open", "resolved", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`tracker text-[10px] px-3 py-1.5 border transition-colors ${filter === f ? "border-brand-light text-brand-light" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`support-inbox-filter-${f}`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <p className="text-xs text-zinc-500" data-testid="support-inbox-empty">No {filter === "all" ? "" : filter + " "}tickets.</p>
      ) : (
        <div className="space-y-2" data-testid="support-inbox-list">
          {visible.map((t) => (
            <div key={t.id} className="border border-white/10 bg-zinc-900/40" data-testid={`support-ticket-${t.id}`}>
              <button
                onClick={() => setExpanded(expanded === t.id ? null : t.id)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                data-testid={`support-ticket-toggle-${t.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{t.summary}</div>
                  <div className="tracker text-[9px] text-zinc-500 mt-1">
                    {t.trainer_name} · {t.trainer_email} · {(t.created_at || "").slice(0, 10)}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`tracker text-[9px] px-2 py-0.5 border ${t.status === "open" ? "border-amber-500/40 text-amber-400" : "border-emerald-500/40 text-emerald-400"}`}>
                    {t.status.toUpperCase()}
                  </span>
                  {expanded === t.id ? <CaretUp size={14} className="text-zinc-500" /> : <CaretDown size={14} className="text-zinc-500" />}
                </div>
              </button>
              {expanded === t.id && (
                <div className="px-4 pb-4 space-y-3" data-testid={`support-ticket-detail-${t.id}`}>
                  <pre className="text-xs text-zinc-400 whitespace-pre-wrap font-sans border border-white/10 bg-[#0A0A0A] p-3 max-h-64 overflow-y-auto">{t.transcript || "(no transcript)"}</pre>
                  {t.status === "open" ? (
                    <button onClick={() => setStatus(t.id, "resolved")} className="btn-primary !py-1.5 !px-3 !text-[10px]" data-testid={`support-ticket-resolve-${t.id}`}>
                      <CheckCircle size={12} className="inline mr-1 -mt-0.5" /> Mark resolved
                    </button>
                  ) : (
                    <button onClick={() => setStatus(t.id, "open")} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`support-ticket-reopen-${t.id}`}>
                      <ArrowCounterClockwise size={12} className="inline mr-1 -mt-0.5" /> Reopen
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
